import React, { useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import styles from './AdminLoginPage.module.scss';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Moon, Sun, Smartphone, Key, CheckCircle } from 'lucide-react';

import { API_BASE } from '../config';
import logger from '../utils/logger';
import { setAuthToken, setOtpUserInfo } from '../utils/authStorage';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  // OTP Login states
  const [loginMode, setLoginMode] = useState('password'); // 'password' or 'otp'
  const [otpData, setOtpData] = useState({
    email: '',
    otp: ''
  });
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  const handleOtpInputChange = (e) => {
    const { id, value } = e.target;
    if (id === 'otpNumber') {
      setOtpData(prev => ({ ...prev, email: value.replace(/\D/g, '') }));
    } else {
      setOtpData(prev => ({ ...prev, otp: value }));
    }
    if (error) setError(null);
  };

  const handleRequestOtp = async () => {
    if (!otpData.email) {
      setError('Please enter your number');
      return;
    }

    setOtpLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/api/otp/request-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: otpData.email,
          purpose: 'login'
        })
      });

      try {
        await response.json();
      } catch (jsonError) {
        // If response is not JSON (e.g., 500 error), still show OTP input
        logger.error('Failed to parse response:', jsonError);
        setOtpSent(true);
        setError('OTP may have been sent. Please check your phone and enter the code.');
        return;
      }

      if (response.ok) {
        setOtpSent(true);
        setError(null);
      } else if (response.status === 404) {
        setOtpSent(false);
        setError('No account found. Please check and try again.');
      } else {
        setOtpSent(true);
        setError('If an account exists, a code was sent. Enter it below.');
      }
    } catch (error) {
      // Always show OTP input field on network error since OTP might have been sent
      logger.error('Network error requesting OTP:', error);
      setOtpSent(true);
      setError('Network error occurred, but OTP may have been sent. Please check your phone and enter the code if received.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpData.otp) {
      setError('Please enter the OTP');
      return;
    }

    setOtpLoading(true);
    setError(null);
    
    try {
      // Directly call login endpoint which handles both verification and login
      const response = await fetch(`${API_BASE}/api/otp/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: otpData.email,
          otp: otpData.otp,
          purpose: 'login'
        })
      });

      let userData = null;
      let errorMessage = null;
      
      // Try to get response as text first to see what we're dealing with
      const responseText = await response.text();
      
      // Try to parse as JSON
      try {
        userData = JSON.parse(responseText);
      } catch (jsonError) {
        logger.error('Failed to parse response as JSON:', jsonError);
        logger.error('Response text:', responseText);
        
        // If response is not JSON, try to extract error message from text
        if (responseText) {
          errorMessage = responseText.length > 200 
            ? responseText.substring(0, 200) + '...' 
            : responseText;
        } else {
          errorMessage = `Server error (${response.status}). Please try again or contact support.`;
        }
      }

      if (!response.ok) {
        // Use error message from JSON if available, otherwise use text or default
        const finalError = userData?.detail || errorMessage || `OTP login failed (${response.status}). Please try again.`;
        setError(finalError);
        logger.error('OTP login failed:', { status: response.status, error: finalError, response: userData });
        return;
      }
      
      // If we got here but userData is null, something went wrong
      if (!userData) {
        setError(errorMessage || 'Invalid response from server. Please try again.');
        return;
      }

      setOtpVerified(true);
      setError(null);
      
      // Check if user is approved
      if (userData.user && userData.user.is_approved) {
        // User is approved, authenticate with Firebase using custom token
        if (userData.custom_token) {
          try {
            const { signInWithCustomToken } = await import('firebase/auth');
            const { auth } = await import('../firebase');
            
            // Sign in with custom token
            const userCredential = await signInWithCustomToken(auth, userData.custom_token);
            const token = await userCredential.user.getIdToken();
            setAuthToken(token);
            logger.debug('Firebase authentication successful:', userCredential.user);
            
            navigate('/dashboard');
          } catch (firebaseError) {
            logger.error('Firebase authentication error:', firebaseError);
            setError('Authentication failed. Please try again.');
          }
        } else {
          // In production, Firebase token is required for security
          const isProduction = process.env.NODE_ENV === 'production';
          
          if (isProduction) {
            logger.error('Firebase token required in production. Authentication failed.');
            setError('Authentication failed. Firebase token is required. Please contact support if this persists.');
            return;
          }
          
          // Development fallback: Allow login without Firebase token (for local testing)
          logger.warn('No custom token provided, but user is approved (development mode only)');
          if (userData.user) {
            setOtpUserInfo(userData.user);
            // Trigger a custom event to notify AuthContext
            window.dispatchEvent(new Event('otp_login_success'));
            // Small delay to ensure AuthContext processes the event before navigation
            setTimeout(() => {
              navigate('/dashboard');
            }, 100);
          } else {
            navigate('/dashboard');
          }
        }
      } else {
        // User needs approval
        setError('Your account is pending admin approval. Please wait for approval before accessing the system.');
      }
      
    } catch (err) {
      logger.error('OTP login error:', err);
      setError(err.message || 'OTP login failed. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };


  const resetOtpForm = () => {
    setOtpData({ email: '', otp: '' });
    setOtpSent(false);
    setOtpVerified(false);
    setError(null);
  };

  // Test backend connection

  // Email/password login
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    
    setEmailLoading(true);
    setError(null);
    
    try {
      logger.debug('Attempting login with:', { email, password: '***' });
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      logger.debug('Login successful:', userCredential.user);
      
      const token = await userCredential.user.getIdToken();
      logger.debug('Token obtained:', token ? 'Yes' : 'No');
      
      setAuthToken(token);
      
      // The PublicRoute component will handle the navigation automatically
      
    } catch (err) {
      logger.error('Login error:', err);
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Invalid email or password. Please try again.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError(err.message || 'Login failed. Please check your credentials.');
      }
    } finally {
      setEmailLoading(false);
    }
  };


  return (
    <div className={styles.loginContainer}>
      <div className={styles.headerBar}>
        <div className={styles.branding}>
          <img src="/company_icon.png" alt="Company Icon" className={styles.companyIcon} />
          <span className={styles.companyTitle}>Blossoms Aroma</span>
        </div>
        <button onClick={toggleTheme} className={styles.themeBtn} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
          {theme === 'dark' ? <Moon /> : <Sun />}
        </button>
      </div>
      <div className={styles.header}>
        <h2>Welcome Back</h2>
        <p>Sign in to your Blossoms Aroma Chemical Inventory account</p>
      </div>
      
      {/* Login Mode Tabs */}
      <div className={styles.loginTabs}>
        <button 
          className={`${styles.tab} ${loginMode === 'password' ? styles.activeTab : ''}`}
          onClick={() => {
            setLoginMode('password');
            resetOtpForm();
          }}
        >
          <Key size={16} />
          Password Login
        </button>
        <button 
          className={`${styles.tab} ${loginMode === 'otp' ? styles.activeTab : ''}`}
          onClick={() => {
            setLoginMode('otp');
            setError(null);
          }}
        >
          <Smartphone size={16} />
          OTP Login
        </button>
      </div>

      {/* Password Login Form */}
      {loginMode === 'password' && (
        <form onSubmit={handleEmailLogin} className={styles.loginForm}>
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className={styles.input}
            required
            disabled={emailLoading}
            placeholder="Enter your email address"
          />
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className={styles.input}
            required
            disabled={emailLoading}
            placeholder="Enter your password"
          />
          <button type="submit" className={styles.loginButton} disabled={emailLoading}>
            {emailLoading ? 'Signing In...' : 'Sign In'}
          </button>
          
          <div className={styles.forgotPassword}>
            <button 
              type="button" 
              className={styles.forgotPasswordLink}
              onClick={() => {
                setLoginMode('otp');
                setOtpData({ email: '', otp: '' });
                setError(null);
              }}
            >
              Forgot Password? Use OTP Login
            </button>
          </div>
        </form>
      )}

      {/* OTP Login Form */}
      {loginMode === 'otp' && (
        <div className={styles.loginForm}>
          {!otpSent && (
            <>
              <label htmlFor="otpNumber">Number</label>
              <div className={styles.inputWithIcon}>
                <Smartphone size={16} className={styles.inputIcon} />
                <input
                  id="otpNumber"
                  type="tel"
                  value={otpData.email}
                  onChange={handleOtpInputChange}
                  className={`${styles.input} ${styles.numberInput}`}
                  placeholder=""
                  autoComplete="off"
                  inputMode="numeric"
                />
              </div>
            </>
          )}

          {!otpSent ? (
            <button 
              type="button" 
              className={styles.otpButton}
              onClick={handleRequestOtp}
              disabled={otpLoading}
            >
              <Smartphone size={16} />
              {otpLoading ? 'Sending OTP...' : 'Send OTP'}
            </button>
          ) : (
            <>
              <p className={styles.otpHint}>Enter the 6-digit code sent to your phone.</p>
              <label htmlFor="otpCode">OTP Code</label>
              <div className={styles.inputWithIcon}>
                <Key size={16} className={styles.inputIcon} />
                <input
                  id="otpCode"
                  type="password"
                  value={otpData.otp}
                  onChange={handleOtpInputChange}
                  className={styles.input}
                  placeholder="Enter 6-digit code"
                  maxLength="6"
                  disabled={otpVerified}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                />
              </div>

              {!otpVerified ? (
                <button 
                  type="button" 
                  className={styles.otpButton}
                  onClick={handleVerifyOtp}
                  disabled={otpLoading}
                >
                  <CheckCircle size={16} />
                  {otpLoading ? 'Logging in...' : 'Login with OTP'}
                </button>
              ) : (
                <div className={styles.successMsg}>
                  <CheckCircle size={16} />
                  Login successful! Redirecting...
                </div>
              )}
            </>
          )}

          {otpSent && !otpVerified && (
            <button 
              type="button" 
              className={styles.secondaryButton}
              onClick={resetOtpForm}
            >
              Start Over
            </button>
          )}
        </div>
      )}
      
      {error && <div className={styles.errorMsg}>{error}</div>}
      
      <div className={styles.registerLink}>
        <p>New user? <button onClick={() => navigate('/register')} className={styles.linkBtn}>Create account here</button></p>
      </div>
      
    </div>
  );
};

export default LoginPage; 